import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Settings, ChevronRight, Plus, Layers, BookOpen, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const FlashcardsDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { tema, subtema } = useParams();
  const decodedTema = tema ? decodeURIComponent(tema) : null;
  const decodedSubtema = subtema ? decodeURIComponent(subtema) : null;
  
  const [groupedData, setGroupedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteDeckConfirm, setShowDeleteDeckConfirm] = useState(null);

  const specialties = [
    'Ginecología y Obstetricia',
    'Cirugía',
    'Pediatría',
    'Medicina Interna',
    'Otros'
  ];

  useEffect(() => {
    fetchFlashcards();
  }, []);

  const fetchFlashcards = async () => {
    try {
      const response = await apiClient.get('/flashcards/grouped');
      setGroupedData(response.data);
    } catch (error) {
      console.error('Error fetching flashcards:', error);
      toast.error('Error al cargar las flashcards');
    } finally {
      setLoading(false);
    }
  };

  const playPencilSound = () => {
    try {
      const audio = new Audio('https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/wfi9fn7o_pencil_scribble_7-389260.mp3');
      audio.volume = 1.0;
      audio.play();
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  const handleNavigate = (path) => {
    playPencilSound();
    setTimeout(() => navigate(path), 100);
  };

  const handleBack = () => {
    if (decodedSubtema) {
      navigate(`/flashcards/${encodeURIComponent(decodedTema)}`);
    } else if (decodedTema) {
      navigate('/flashcards');
    } else {
      navigate('/');
    }
  };

  const getCardCount = (specialty) => {
    return groupedData?.[specialty]?.count || 0;
  };

  const getSubtemaCount = (specialty, subtemaName) => {
    return groupedData?.[specialty]?.topics?.[subtemaName]?.count || 0;
  };

  const getDeckCount = (specialty, subtemaName, deckName) => {
    return groupedData?.[specialty]?.topics?.[subtemaName]?.quizzes?.[deckName]?.count || 0;
  };

  const getTotalCards = () => {
    if (!groupedData) return 0;
    return Object.values(groupedData).reduce((total, spec) => total + (spec.count || 0), 0);
  };

  const handleDeleteDeck = async (deckName) => {
    try {
      await apiClient.delete('/flashcards/deck', {
        params: {
          topic: decodedSubtema,
          quiz_title: deckName
        }
      });
      toast.success('Paquete eliminado exitosamente');
      setShowDeleteDeckConfirm(null);
      fetchFlashcards();
    } catch (error) {
      console.error('Error deleting deck:', error);
      toast.error('Error al eliminar el paquete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Render Level 3: Decks (cuestionarios)
  if (decodedTema && decodedSubtema) {
    const decks = groupedData?.[decodedTema]?.topics?.[decodedSubtema]?.quizzes || {};
    const deckNames = Object.keys(decks);

    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Volver</span>
            </button>
            
            <div className="flex items-center space-x-2">
              {user.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
                >
                  <Settings size={14} className="text-black" />
                </button>
              )}
              <button
                onClick={onLogout}
                className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              >
                <LogOut size={14} className="text-black" />
              </button>
            </div>
          </div>
        </header>

        <div className="pt-20 pb-8 px-4">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black mb-2 text-center">
              {decodedSubtema}
            </h1>
            <p className="text-gray-500 text-center mb-6 text-sm">{decodedTema}</p>

            {/* Add Flashcard Button */}
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full mb-6 border-2 border-dashed border-gray-300 rounded-xl p-4 flex items-center justify-center space-x-2 text-gray-600 hover:border-black hover:text-black transition-all"
            >
              <Plus size={20} />
              <span>Agregar flashcard</span>
            </button>

            {deckNames.length === 0 ? (
              <div className="text-center py-12">
                <Layers size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No hay paquetes de flashcards aún</p>
                <p className="text-gray-400 text-sm mt-2">Guarda preguntas desde los cuestionarios o crea flashcards manuales</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deckNames.map((deckName, index) => {
                  const count = getDeckCount(decodedTema, decodedSubtema, deckName);
                  const isHovered = hoveredItem === deckName;
                  
                  return (
                    <div
                      key={deckName}
                      onMouseEnter={() => setHoveredItem(deckName)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`w-full border rounded-xl p-4 transition-all duration-200 ${
                        isHovered ? 'bg-white border-black shadow-md' : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Icon column with delete button below */}
                        <div className="flex-shrink-0 flex flex-col items-center space-y-2">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                            isHovered ? 'bg-black' : 'bg-gray-100'
                          }`}>
                            <Layers size={20} className={isHovered ? 'text-white' : 'text-gray-600'} />
                          </div>
                          {/* Delete button below icon */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeleteDeckConfirm(deckName);
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar paquete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        
                        {/* Content - clickable area */}
                        <button
                          onClick={() => handleNavigate(`/flashcards/deck/${encodeURIComponent(decodedTema)}/${encodeURIComponent(decodedSubtema)}/${encodeURIComponent(deckName)}`)}
                          className="flex-1 text-left min-w-0"
                        >
                          <h3 className="text-sm sm:text-base font-semibold text-black text-left break-words">
                            Flash Cards + {deckName}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-500 mt-1">
                            {count} {count === 1 ? 'tarjeta' : 'tarjetas'}
                          </p>
                        </button>
                        
                        {/* Chevron */}
                        <div className="flex-shrink-0 self-center">
                          <ChevronRight size={20} className={`transition-all duration-200 ${isHovered ? 'text-black translate-x-1' : 'text-gray-400'}`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Delete Deck Confirmation Modal */}
        {showDeleteDeckConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-bold text-black mb-2">¿Eliminar paquete completo?</h3>
              <p className="text-gray-600 mb-4">
                Se eliminarán todas las flashcards de "{showDeleteDeckConfirm}". Esta acción no se puede deshacer.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteDeckConfirm(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDeleteDeck(showDeleteDeckConfirm)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Eliminar todo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Form Modal */}
        {showCreateForm && (
          <FlashcardFormModal
            onClose={() => setShowCreateForm(false)}
            onSuccess={() => {
              setShowCreateForm(false);
              fetchFlashcards();
            }}
            defaultSpecialty={decodedTema}
            defaultTopic={decodedSubtema}
          />
        )}
      </div>
    );
  }

  // Render Level 2: Subtemas (topics)
  if (decodedTema) {
    const topics = groupedData?.[decodedTema]?.topics || {};
    const topicNames = Object.keys(topics);

    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Volver</span>
            </button>
            
            <div className="flex items-center space-x-2">
              {user.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
                >
                  <Settings size={14} className="text-black" />
                </button>
              )}
              <button
                onClick={onLogout}
                className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              >
                <LogOut size={14} className="text-black" />
              </button>
            </div>
          </div>
        </header>

        <div className="pt-20 pb-8 px-4">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black mb-2 text-center">
              {decodedTema}
            </h1>
            <p className="text-gray-500 text-center mb-6 text-sm">
              {getCardCount(decodedTema)} tarjetas en total
            </p>

            {topicNames.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No hay subtemas con flashcards aún</p>
                <p className="text-gray-400 text-sm mt-2">Guarda preguntas desde los cuestionarios</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topicNames.map((topicName, index) => {
                  const count = getSubtemaCount(decodedTema, topicName);
                  const isHovered = hoveredItem === topicName;
                  
                  return (
                    <button
                      key={topicName}
                      onClick={() => handleNavigate(`/flashcards/${encodeURIComponent(decodedTema)}/${encodeURIComponent(topicName)}`)}
                      onMouseEnter={() => setHoveredItem(topicName)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`w-full border rounded-xl p-4 transition-all duration-200 text-left flex items-center space-x-3 ${
                        isHovered ? 'bg-white border-black shadow-md' : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-1">
                        <span className={`relative flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                          isHovered ? 'border-black' : 'border-gray-400'
                        }`}>
                          <span className={`absolute w-3 h-3 rounded-full transition-all duration-200 ${
                            isHovered ? 'bg-black scale-100 opacity-100' : 'scale-0 opacity-0'
                          }`}></span>
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm sm:text-base font-semibold transition-colors duration-200 ${
                          isHovered ? 'text-black' : 'text-gray-800'
                        }`}>
                          {topicName}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {count} {count === 1 ? 'tarjeta' : 'tarjetas'}
                        </p>
                      </div>
                      <ChevronRight size={20} className={`transition-all duration-200 ${isHovered ? 'text-black translate-x-1' : 'text-gray-400'}`} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render Level 1: Specialties (temas base)
  const totalCards = getTotalCards();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Volver</span>
          </button>
          
          <div className="flex items-center space-x-2">
            {user.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              >
                <Settings size={14} className="text-black" />
              </button>
            )}
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </div>
      </header>

      <div className="pt-20 pb-8 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black mb-2 text-center">
            Flash cards personales
          </h1>
          <p className="text-gray-500 text-center mb-6 text-sm">
            {totalCards} {totalCards === 1 ? 'tarjeta' : 'tarjetas'} guardadas
          </p>

          {/* Add Flashcard Button */}
          <button
            onClick={() => setShowCreateForm(true)}
            className="w-full mb-6 border-2 border-dashed border-gray-300 rounded-xl p-4 flex items-center justify-center space-x-2 text-gray-600 hover:border-black hover:text-black transition-all"
          >
            <Plus size={20} />
            <span>Agregar flashcard</span>
          </button>

          {totalCards === 0 ? (
            <div className="text-center py-12">
              <Layers size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-2">No tienes flashcards guardadas</p>
              <p className="text-gray-400 text-sm">Empieza guardando preguntas desde un cuestionario</p>
            </div>
          ) : (
            <div className="space-y-3">
              {specialties.map((specialty, index) => {
                const count = getCardCount(specialty);
                const isHovered = hoveredItem === specialty;
                const hasCards = count > 0;
                
                return (
                  <button
                    key={specialty}
                    onClick={() => hasCards && handleNavigate(`/flashcards/${encodeURIComponent(specialty)}`)}
                    onMouseEnter={() => setHoveredItem(specialty)}
                    onMouseLeave={() => setHoveredItem(null)}
                    disabled={!hasCards}
                    className={`w-full border rounded-xl p-4 transition-all duration-200 text-left flex items-center space-x-3 ${
                      !hasCards
                        ? 'bg-gray-50 border-gray-100 cursor-not-allowed opacity-50'
                        : isHovered
                          ? 'bg-white border-black shadow-md'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-1">
                      <span className={`relative flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                        !hasCards ? 'border-gray-300' : isHovered ? 'border-black' : 'border-gray-400'
                      }`}>
                        <span className={`absolute w-3 h-3 rounded-full transition-all duration-200 ${
                          isHovered && hasCards ? 'bg-black scale-100 opacity-100' : 'scale-0 opacity-0'
                        }`}></span>
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-sm sm:text-base font-semibold transition-colors duration-200 ${
                        !hasCards ? 'text-gray-400' : isHovered ? 'text-black' : 'text-gray-800'
                      }`}>
                        {specialty}
                      </h3>
                      <p className={`text-xs sm:text-sm ${
                        hasCards ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {count} {count === 1 ? 'tarjeta' : 'tarjetas'}
                      </p>
                    </div>
                    {hasCards && (
                      <ChevronRight size={20} className={`transition-all duration-200 ${isHovered ? 'text-black translate-x-1' : 'text-gray-400'}`} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <FlashcardFormModal
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            fetchFlashcards();
          }}
        />
      )}
    </div>
  );
};

// Inline Form Modal Component
const FlashcardFormModal = ({ onClose, onSuccess, defaultSpecialty = '', defaultTopic = '' }) => {
  const [formData, setFormData] = useState({
    specialty: defaultSpecialty,
    topic: defaultTopic,
    quiz_title: '',
    question_text: '',
    answer_text: '',
    personal_notes: ''
  });
  const [availableTopics, setAvailableTopics] = useState([]);
  const [availableDecks, setAvailableDecks] = useState([]);
  const [isNewDeck, setIsNewDeck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const specialties = [
    'Ginecología y Obstetricia',
    'Cirugía',
    'Pediatría',
    'Medicina Interna',
    'Otros'
  ];

  useEffect(() => {
    if (formData.specialty) {
      fetchTopics(formData.specialty);
    }
  }, [formData.specialty]);

  useEffect(() => {
    if (formData.specialty && formData.topic) {
      fetchDecks(formData.specialty, formData.topic);
    }
  }, [formData.specialty, formData.topic]);

  const fetchTopics = async (specialty) => {
    try {
      const response = await apiClient.get(`/flashcards/topics/${encodeURIComponent(specialty)}`);
      setAvailableTopics(response.data);
    } catch (error) {
      console.error('Error fetching topics:', error);
    }
  };

  const fetchDecks = async (specialty, topic) => {
    try {
      const response = await apiClient.get(`/flashcards/decks/${encodeURIComponent(specialty)}/${encodeURIComponent(topic)}`);
      setAvailableDecks(response.data);
    } catch (error) {
      console.error('Error fetching decks:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.specialty || !formData.topic || !formData.quiz_title || !formData.question_text || !formData.answer_text) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/flashcards/manual', formData);
      toast.success('Flashcard creada exitosamente');
      onSuccess();
    } catch (error) {
      console.error('Error creating flashcard:', error);
      toast.error('Error al crear la flashcard');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold text-black mb-4">Nueva Flashcard</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Specialty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tema base *</label>
              <select
                value={formData.specialty}
                onChange={(e) => setFormData({ ...formData, specialty: e.target.value, topic: '', quiz_title: '' })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-black"
                required
              >
                <option value="">Selecciona un tema</option>
                {specialties.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Topic */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtema *</label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => setFormData({ ...formData, topic: e.target.value, quiz_title: '' })}
                placeholder="Ej: Cardiología"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-black"
                list="topics-list"
                required
              />
              <datalist id="topics-list">
                {availableTopics.map(t => <option key={t} value={t} />)}
              </datalist>
            </div>

            {/* Quiz Title / Deck */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Paquete / Cuestionario *</label>
              <input
                type="text"
                value={formData.quiz_title}
                onChange={(e) => setFormData({ ...formData, quiz_title: e.target.value })}
                placeholder="Ej: Hipotiroidismo"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-black"
                list="decks-list"
                required
              />
              <datalist id="decks-list">
                {availableDecks.map(d => <option key={d} value={d} />)}
              </datalist>
            </div>

            {/* Question */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pregunta *</label>
              <textarea
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                placeholder="Escribe la pregunta..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-black resize-none"
                required
              />
            </div>

            {/* Answer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Respuesta *</label>
              <textarea
                value={formData.answer_text}
                onChange={(e) => setFormData({ ...formData, answer_text: e.target.value })}
                placeholder="Escribe la respuesta..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-black resize-none"
                required
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas personales</label>
              <textarea
                value={formData.personal_notes}
                onChange={(e) => setFormData({ ...formData, personal_notes: e.target.value })}
                placeholder="Notas adicionales (opcional)"
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-black resize-none"
              />
            </div>

            {/* Buttons */}
            <div className="flex space-x-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FlashcardsDashboard;
